import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSpeechSynthesis } from 'react-speech-kit';
import ToolLayout from 'tools/ToolLayout';
import Colors from 'theme/colors';
import {
  btnDisabled,
  btnRow,
  card,
  chipScrollRow,
  chipStyle,
  dangerBtn,
  errorBox,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  textArea,
  touchBtnFull,
  warnBox,
} from 'tools/toolStyles';
import { DiffResult, WordResult, alignWords } from './diff';
import { MicRecorder, createMicRecorder } from './record';
import { ensureTranscriber, isModelReady, transcribe } from './whisper';

const MAX_SEC = 15;
const MAX_WORDS = 25;

const SUGGESTIONS = [
  'Good morning. How are you today?',
  'Could you please repeat that?',
  'I would like a cup of coffee.',
  'The meeting starts at nine o\'clock.',
  'Please send me the report by Friday.',
  'Thank you for your help.',
  'Where is the nearest subway station?',
  'I am looking forward to working with you.',
  'The price includes tax and shipping.',
  'Let me check my schedule and get back to you.',
];

const wordChipBase: any = {
  display: 'inline-block',
  padding: '4px 8px',
  margin: '3px 4px',
  borderRadius: 6,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.4,
};

function wordStyle(tag: string): any {
  if (tag === 'ok') {
    return { ...wordChipBase, background: '#e8f5e9', color: '#1b5e20' };
  }
  if (tag === 'near') {
    return { ...wordChipBase, background: '#fff8e1', color: '#f57f17' };
  }
  if (tag === 'wrong' || tag === 'missing') {
    return { ...wordChipBase, background: '#ffebee', color: '#b71c1c' };
  }
  if (tag === 'extra') {
    return { ...wordChipBase, background: '#eceff1', color: '#546e7a', textDecoration: 'line-through' };
  }
  return wordChipBase;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

const Pronunciation = () => {
  const { speak, speaking, cancel } = useSpeechSynthesis();
  const recorderRef = useRef<MicRecorder | null>(null);
  const tickRef = useRef<any>(null);

  const [narrow, setNarrow] = useState(false);
  const [expected, setExpected] = useState(SUGGESTIONS[0]);
  const [recState, setRecState] = useState<'idle' | 'recording' | 'ready'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [pcm, setPcm] = useState<Float32Array | null>(null);
  const [status, setStatus] = useState('');
  const [loadPct, setLoadPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [transcript, setTranscript] = useState('');
  const [diff, setDiff] = useState<DiffResult | null>(null);
  const [practiceWord, setPracticeWord] = useState<string | null>(null);

  useEffect(() => {
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    const measure = () => {
      const w = win.innerWidth != null ? win.innerWidth : 800;
      setNarrow(w < 520);
    };
    measure();
    if (win.addEventListener) {
      win.addEventListener('resize', measure);
      return () => {
        win.removeEventListener('resize', measure);
        if (tickRef.current) clearInterval(tickRef.current);
        if (speaking) cancel();
      };
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (speaking) cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wordCount = useMemo(() => countWords(expected), [expected]);
  const tooLong = wordCount > MAX_WORDS;

  const listenSample = (text?: string) => {
    const t = (text || expected || '').trim();
    if (!t) return;
    try {
      cancel();
    } catch {
      /* ignore */
    }
    speak({ text: t, rate: 0.9 });
  };

  const startRec = async () => {
    setError('');
    setDiff(null);
    setTranscript('');
    setPcm(null);
    setPracticeWord(null);
    try {
      if (!recorderRef.current) recorderRef.current = createMicRecorder(MAX_SEC);
      await recorderRef.current.start();
      setRecState('recording');
      setElapsed(0);
      if (tickRef.current) clearInterval(tickRef.current);
      const started = Date.now();
      tickRef.current = setInterval(() => {
        const sec = Math.floor((Date.now() - started) / 1000);
        setElapsed(sec);
        if (sec >= MAX_SEC) {
          stopRec();
        }
      }, 200);
    } catch (err: any) {
      setError((err && err.message) || 'Không bắt đầu được ghi âm.');
      setRecState('idle');
    }
  };

  const stopRec = async () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const rec = recorderRef.current;
    if (!rec) {
      setRecState('idle');
      return;
    }
    try {
      const data = await rec.stop();
      setPcm(data);
      setRecState(data && data.length ? 'ready' : 'idle');
      if (!data || !data.length) {
        setError('Không thu được âm thanh. Hãy thử lại gần micro hơn.');
      }
    } catch (err: any) {
      setError((err && err.message) || 'Dừng ghi thất bại.');
      setRecState('idle');
    }
  };

  const preloadModel = async () => {
    setBusy(true);
    setError('');
    try {
      await ensureTranscriber((info) => {
        setStatus(info.status);
        if (typeof info.progress === 'number') setLoadPct(info.progress);
      });
      setStatus('Model sẵn sàng.');
      setLoadPct(1);
    } catch (err: any) {
      setError((err && err.message) || 'Tải model thất bại.');
      setStatus('');
    } finally {
      setBusy(false);
    }
  };

  const score = async (targetText?: string) => {
    const target = (targetText || expected || '').trim();
    if (!target) {
      setError('Hãy nhập câu mẫu.');
      return;
    }
    if (!pcm || !pcm.length) {
      setError('Hãy thu âm trước khi chấm điểm.');
      return;
    }
    setBusy(true);
    setError('');
    setDiff(null);
    try {
      const text = await transcribe(pcm, (info) => {
        setStatus(info.status);
        if (typeof info.progress === 'number') setLoadPct(info.progress);
      });
      setTranscript(text);
      const result = alignWords(target, text);
      setDiff(result);
      setStatus('Xong.');
    } catch (err: any) {
      setError((err && err.message) || 'Chấm điểm thất bại.');
      setStatus('');
    } finally {
      setBusy(false);
    }
  };

  const startPracticeWord = async (word: string) => {
    setPracticeWord(word);
    setExpected(word);
    setDiff(null);
    setTranscript('');
    setPcm(null);
    setRecState('idle');
    listenSample(word);
  };

  const legend: any = {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
  };

  return (
    <ToolLayout title="Kiểm tra phát âm" showBackToToeic>
      <p style={privacy}>
        Âm thanh được xử lý hoàn toàn trên máy bạn. Model AI (Whisper tiny) tải về và lưu cache trong trình duyệt —
        không gửi lên server language123.net.
      </p>
      <p style={warnBox}>
        Kết quả mang tính tham khảo để luyện tập — model nhỏ, không phải bài chấm thi chính thức. Dùng Chrome/Edge
        (hoặc Safari mới) trên điện thoại; hãy cho phép micro khi trình duyệt hỏi.
      </p>

      <div style={card}>
        <label style={label}>1. Câu mẫu tiếng Anh</label>
        <textarea
          value={expected}
          onChange={(e: any) => setExpected(e.target.value)}
          rows={3}
          style={textArea}
          placeholder="Nhập câu bạn muốn luyện…"
        />
        <p style={{ ...hint, marginTop: 8 }}>
          {wordCount} từ · gợi ý ≤ {MAX_WORDS} từ, nói ≤ {MAX_SEC}s
        </p>
        {tooLong ? <p style={warnBox}>Câu hơi dài — Whisper tiny chấm tốt hơn với câu ngắn.</p> : null}

        <label style={label}>Gợi ý nhanh</label>
        <div style={chipScrollRow}>
          {SUGGESTIONS.map((s, i) => (
            <button
              key={s}
              type="button"
              title={s}
              aria-label={s}
              style={chipStyle(expected === s)}
              onClick={() => {
                setExpected(s);
                setDiff(null);
                setTranscript('');
                setPracticeWord(null);
              }}
            >
              {`Câu ${i + 1}`}
            </button>
          ))}
        </div>

        <div style={btnRow}>
          <button
            type="button"
            style={{
              ...(speaking || !expected.trim() ? btnDisabled(secondaryBtn) : secondaryBtn),
              ...(narrow ? touchBtnFull : {}),
            }}
            disabled={!expected.trim() || speaking}
            onClick={() => listenSample()}
          >
            {speaking ? 'Đang phát…' : 'Nghe mẫu (TTS)'}
          </button>
        </div>
      </div>

      <div style={card}>
        <label style={label}>2. Thu âm của bạn</label>
        <p style={hint}>Bấm Ghi rồi đọc to câu mẫu vào micro. Tối đa {MAX_SEC} giây.</p>
        <div style={btnRow}>
          {recState !== 'recording' ? (
            <button
              type="button"
              style={{ ...(busy ? btnDisabled(primaryBtn) : primaryBtn), ...(narrow ? touchBtnFull : {}) }}
              disabled={busy}
              onClick={startRec}
            >
              Ghi âm
            </button>
          ) : (
            <button
              type="button"
              style={{ ...dangerBtn, ...(narrow ? touchBtnFull : {}) }}
              onClick={stopRec}
            >
              Dừng ({elapsed}s / {MAX_SEC}s)
            </button>
          )}
          {recState === 'ready' && pcm ? (
            <span style={{ ...hint, margin: 0, alignSelf: 'center' }}>
              Đã thu ~{(pcm.length / 16000).toFixed(1)}s — sẵn sàng chấm
            </span>
          ) : null}
        </div>
      </div>

      <div style={card}>
        <label style={label}>3. Chấm điểm</label>
        <div style={btnRow}>
          <button
            type="button"
            style={{
              ...(busy || isModelReady() ? btnDisabled(secondaryBtn) : secondaryBtn),
              ...(narrow ? touchBtnFull : {}),
            }}
            disabled={busy || isModelReady()}
            onClick={preloadModel}
          >
            {isModelReady() ? 'Model đã sẵn sàng' : 'Tải model trước (tuỳ chọn)'}
          </button>
          <button
            type="button"
            style={{
              ...(busy || recState !== 'ready' || !pcm ? btnDisabled(primaryBtn) : primaryBtn),
              ...(narrow ? touchBtnFull : {}),
            }}
            disabled={busy || recState !== 'ready' || !pcm}
            onClick={() => score(practiceWord || expected)}
          >
            {busy ? 'Đang xử lý…' : 'Chấm điểm'}
          </button>
        </div>
        {(busy || loadPct > 0) && loadPct < 1 ? (
          <div style={{ height: 8, background: '#eceff1', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <div
              style={{
                width: `${Math.round(loadPct * 100)}%`,
                height: '100%',
                background: Colors.MAIN_COLOR,
                transition: 'width 0.2s',
              }}
            />
          </div>
        ) : null}
        {status ? <p style={hint}>{status}</p> : null}
        {error ? <p style={errorBox}>{error}</p> : null}

        {diff ? (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: Colors.MAIN_COLOR, marginBottom: 8 }}>
              {diff.scorePct}%{' '}
              <span style={{ fontSize: 14, fontWeight: 600, color: '#666' }}>
                ({diff.okCount}/{diff.totalExpected} từ đúng)
              </span>
            </div>
            <label style={label}>Câu mẫu (tô màu)</label>
            <div>
              {diff.words.map((w: WordResult, idx: number) =>
                w.expected ? (
                  <span key={idx} style={wordStyle(w.tag)} title={w.heard ? `Nghe: ${w.heard}` : 'Thiếu'}>
                    {w.expected}
                  </span>
                ) : (
                  <span key={idx} style={wordStyle('extra')} title="Thừa">
                    +{w.heard}
                  </span>
                )
              )}
            </div>
            <p style={legend}>
              <span style={wordStyle('ok')}>đúng</span>
              <span style={wordStyle('near')}>gần đúng</span>
              <span style={wordStyle('wrong')}>sai / thiếu</span>
              <span style={wordStyle('extra')}>thừa</span>
            </p>
            {transcript ? (
              <>
                <label style={{ ...label, marginTop: 12 }}>AI nghe được</label>
                <p style={{ ...hint, margin: 0, fontStyle: 'italic' }}>&ldquo;{transcript}&rdquo;</p>
              </>
            ) : null}

            {diff.wrongWords.length ? (
              <>
                <label style={{ ...label, marginTop: 16 }}>4. Luyện từ sai</label>
                <p style={hint}>Chọn một từ → nghe mẫu → ghi lại → chấm điểm riêng từ đó.</p>
                <div style={btnRow}>
                  {diff.wrongWords.map((w) => (
                    <button
                      key={w}
                      type="button"
                      style={chipStyle(practiceWord === w)}
                      onClick={() => startPracticeWord(w)}
                    >
                      {w}
                    </button>
                  ))}
                </div>
                {practiceWord ? (
                  <p style={{ ...hint, marginBottom: 0 }}>
                    Đang luyện từ: <strong>{practiceWord}</strong> — ghi âm rồi bấm Chấm điểm.
                  </p>
                ) : null}
              </>
            ) : (
              <p style={{ ...hint, marginTop: 12, color: '#1b5e20', fontWeight: 700 }}>Tuyệt vời — không còn từ sai!</p>
            )}
          </div>
        ) : null}
      </div>
    </ToolLayout>
  );
};

export default Pronunciation;
